import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Handle hash navigation when arriving at homepage
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const id = location.hash.replace('#', '');
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const navLinks = [
    { label: 'Features', href: '/#product' },
    { label: 'How it works', href: '/#how-it-works' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'Support', href: '/support' },
    { label: 'Contact', href: '/contact' },
  ];

  const handleNavClick = (e, href) => {
    if (href.startsWith('/#')) {
      e.preventDefault();
      const id = href.replace('/#', '');
      
      if (location.pathname === '/') {
        // Already on homepage, scroll to section
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        // Navigate to homepage with hash
        navigate('/', { state: { scrollTo: id } });
        // Also set the hash for fallback
        setTimeout(() => {
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      }
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-sm border-b border-[#E8E4DF]' : 'bg-white'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <nav className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-9 h-9 bg-[#1A1411] rounded flex items-center justify-center">
              <span className="text-white font-bold text-sm">Q1</span>
            </div>
            <div className="hidden sm:block">
              <span className="text-[#1A1411] font-medium">QIndex One</span>
              <span className="text-[#9B8B7E] text-sm ml-2">Client Intelligence Platform</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-[#6B5B52] hover:text-[#A86A3A] transition-colors text-sm"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/portal/login"
              className="text-[#6B5B52] hover:text-[#A86A3A] transition-colors text-sm"
              data-testid="nav-portal-btn"
            >
              Portal
            </Link>
            <Link 
              to="/login" 
              className="text-[#6B5B52] hover:text-[#1A1411] transition-colors text-sm"
              data-testid="nav-login-btn"
            >
              Login
            </Link>
            <Link 
              to="/pilot" 
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#A86A3A] text-white text-sm font-medium rounded hover:bg-[#8B5730] transition-colors"
              data-testid="nav-pilot-btn"
            >
              Request a pilot
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-[#1A1411] hover:bg-[#F5F3F0] rounded transition-colors"
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>

        {/* Mobile Menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-96 pb-6' : 'max-h-0'
        }`}>
          <div className="flex flex-col gap-1 pt-2 border-t border-[#E8E4DF]">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-[#6B5B52] hover:text-[#A86A3A] hover:bg-[#FAF8F5] transition-colors py-3 px-4 rounded"
              >
                {link.label}
              </a>
            ))}
            <div className="border-t border-[#E8E4DF] mt-2 pt-4 px-4 flex flex-col gap-3">
              <Link 
                to="/login" 
                className="text-[#6B5B52] py-2"
              >
                Login
              </Link>
              <Link 
                to="/pilot" 
                className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#A86A3A] text-white font-medium rounded hover:bg-[#8B5730] transition-colors"
              >
                Request a pilot
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Navigation;
